import React, { useState, useEffect, useRef } from 'react';
import { Save, Plus, User, X, Delete } from 'lucide-react';
import { useLanguage } from '../StoreContext/LanguageContext';
import apiService from '../config';

const EditLandholdingList = ({ onBack, onSuccess, LandAcquisition_id }) => {
    const { language, translations } = useLanguage();

    const [formData, setFormData] = useState({
        project_id: '',
        village_name: '',
        dist_name: '',
        taluka_name: '',
        landholder_name: '',
        survey_no: '',
        survey_type: '',
        irrigable_type: '',
        irrigable_area: 0,
        non_irrigable_area: 0,
        total_area: 0,
        total_7_12_area: '',
        common_area: '',
        north: '',
        east: '',
        west: '',
        south: '',
        area_unit_id: '',
        aqr_area: 0,
        unit_name: 'ACRE',
        is_active: 1,
        land_holding_type: '',
        house_owner_type: '',
        house_total_area: '',
        construction_area: '',
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [currentLandId, setCurrentLandId] = useState(null);

    // Owner/Occupant states
    const [existingOwners, setExistingOwners] = useState([]);
    const [showAddOwnerForm, setShowAddOwnerForm] = useState(false);
    const [addingOwner, setAddingOwner] = useState(false);
    const [newOwnerData, setNewOwnerData] = useState({
    owner_name: '',
    survey_no: '',
    owner_total_area: '',
    owner_aqr_area: '',
    owner_irrigable_area: '',
    owner_non_irrigable_area: '',
    owner_construction_area: '',
    });

    const [showEditOwnerForm, setShowEditOwnerForm] = useState(false);
    const [editingOwner, setEditingOwner] = useState(false);
    const [editOwnerData, setEditOwnerData] = useState({
    land_owner_id: '',
    owner_name: '',
    survey_no: '',
    owner_total_area: '',
    owner_aqr_area: '',
    owner_irrigable_area: '',
    owner_non_irrigable_area: '',
    owner_construction_area: '',
    });
    const [currentEditingOwnerId, setCurrentEditingOwnerId] = useState(null);
    const [originalOwnerData, setOriginalOwnerData] = useState(null);

    // Area tracking states
    const [totalCompletedOwnerArea, setTotalCompletedOwnerArea] = useState(0);
    const [remainingTotalArea, setRemainingTotalArea] = useState(0);
    const [isTotalAreaComplete, setIsTotalAreaComplete] = useState(false);
    const [totalCompletedAcquiredArea, setTotalCompletedAcquiredArea] = useState(0);
    const [remainingAcquiredArea, setRemainingAcquiredArea] = useState(0);
    const [isAcquiredAreaComplete, setIsAcquiredAreaComplete] = useState(false);

    // Modal states
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({ title: '', message: '', onConfirm: null, onCancel: null, type: 'warning' });

    const addOwnerFormRef = useRef(null);
    const editOwnerFormRef = useRef(null);

    const normalizeSurveyType = (value = '') => value.replace(/\./g, '').trim();
    const isHouseNumber = normalizeSurveyType(formData.survey_type) === 'घर क्रमांक';

    const safeTwoDecimals = (val) => {
        const num = parseFloat(val);
        if (isNaN(num)) return '';
        return parseFloat(num.toFixed(4));
    };

    // ─── Area calculations ─────────────────────────────────────

    const calculateTotalAreaCompletion = (owners = existingOwners) => {
        const totalCompleted = owners.reduce((sum, o) => sum + (parseFloat(o.owner_total_area) || 0), 0);
        const landTotalArea = parseFloat(formData.total_area) || 0;
        setTotalCompletedOwnerArea(totalCompleted);
        setRemainingTotalArea(Math.max(0, landTotalArea - totalCompleted));
        setIsTotalAreaComplete(totalCompleted >= landTotalArea);
    };

    const calculateAcquiredAreaCompletion = (owners = existingOwners) => {
        const acquiredCompleted = owners.reduce((sum, o) => sum + (parseFloat(o.owner_aqr_area) || 0), 0);
        const landAcquiredArea = parseFloat(formData.aqr_area) || 0;
        setTotalCompletedAcquiredArea(acquiredCompleted);
        setRemainingAcquiredArea(Math.max(0, landAcquiredArea - acquiredCompleted));
        setIsAcquiredAreaComplete(acquiredCompleted >= landAcquiredArea);
    };

    useEffect(() => {
        calculateTotalAreaCompletion();
        calculateAcquiredAreaCompletion();
    }, [existingOwners, formData.total_area, formData.aqr_area]);

    // ─── Data fetching ─────────────────────────────────────────

    useEffect(() => { initializeComponent(); }, [LandAcquisition_id]);

    const initializeComponent = async () => {
        try {
            setLoading(true);
            setError(null);
            let landIdToUse = null;
            let dataToLoad = null;

            if (LandAcquisition_id && LandAcquisition_id !== 'undefined') {
                landIdToUse = LandAcquisition_id;
            } else {
                const editingData = localStorage.getItem('editingLandholding');
                if (editingData) {
                    try {
                        dataToLoad = JSON.parse(editingData);
                        landIdToUse = dataToLoad.groupNo || dataToLoad.LandAcquisition_id;
                    } catch {
                        setError('Failed to load landholding data from storage.');
                        setLoading(false);
                        return;
                    }
                }
            }

            if (!landIdToUse || landIdToUse === 'undefined') {
                setError('No valid land ID found. Cannot load landholding data.');
                setLoading(false);
                return;
            }

            setCurrentLandId(landIdToUse);

            try {
                await fetchLandholdingData(landIdToUse);
            } catch {
                if (dataToLoad) mapDataToFormState(dataToLoad);
                else throw new Error('Failed to fetch data');
            }
        } catch (err) {
            console.error('Error initializing component:', err);
            setError('Failed to initialize edit form. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const mapDataToFormState = (data) => {
        const record = Array.isArray(data) ? data[0] : data;
        setFormData({
            project_id: record.project_id || '',
            proposal_id: record.proposal_id || '',
            district_id: record.dist_name || record.district_id || '',
            taluka_id: record.taluka_name || record.taluka_id || '',
            village_id: record.village_name || record.village_id || '',
            dist_name: record.dist_name || '',
            taluka_name: record.taluka_name || '',
            village_name: record.village_name || '',
            LandAcquisition_id: record.groupNo || record.LandAcquisition_id || '',
            landholder_name: record.landholdersName || record.landholder_name || '',
            survey_no: record.SurveyNO || record.survey_no || '',
            survey_type: normalizeSurveyType(record.survey_type),
            irrigable_type: record.irrigable_type || 'Canal',
            irrigable_area: parseFloat(record.irrigableArea || record.irrigable_area) || 0,
            non_irrigable_area: parseFloat(record.nonIrrigableArea || record.non_irrigable_area) || 0,
            total_area: parseFloat(record.totalArea || record.total_area) || 0,
            total_7_12_area: parseFloat(record.total_7_12_area) || '',
            common_area: record.common_area || '',
            aqr_area: parseFloat(record.AquiredArea || record.aqr_area) || 0,
            construction_area: record.construction_area || '',
            unit_name: record.unit_name || 'ACRE',
            north: record.north || '',
            east: record.east || '',
            west: record.west || '',
            south: record.south || '',
            area_unit_id: record.area_unit_id || '',
            is_active: record.status !== undefined ? (record.status ? 1 : 0) : (record.is_active !== undefined ? record.is_active : 1),
            land_holding_type: record.land_holding_type || '',
        });
        if (record.owners && Array.isArray(record.owners)) {
            setExistingOwners(record.owners);
        }
    };

    const fetchLandholdingData = async (id) => {
        if (!id || id === 'undefined') throw new Error('No valid land ID.');
        const result = await apiService.getLandHoldingById(id);
        if (!result || (!result.ok && result.status !== 200)) throw new Error(`HTTP error: ${result?.status}`);
        const data = result.data || result;
        if (!data) throw new Error('No data received');
        mapDataToFormState(data);
    };

    // ─── Main form: only total_7_12_area and common_area are editable ─────────

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!currentLandId) {
            setError('No valid land ID available. Cannot proceed with save.');
            return;
        }
        try {
            setSaving(true);
            setError(null);
            const result = await apiService.updateLandHolding(currentLandId, {
                total_7_12_area: formData.total_7_12_area,
                common_area: formData.common_area,
                north: formData.north,
                east: formData.east,
                west: formData.west,
                south: formData.south,
            });
            if (result && (result.ok || result.status === 200)) {
                localStorage.removeItem('editingLandholding');
                if (typeof onSuccess === 'function') onSuccess();
                setTimeout(() => fetchLandholdingData(currentLandId), 1000);
            } else {
                throw new Error('Update failed - invalid response from server');
            }
        } catch (err) {
            console.error('Error updating landholding:', err);
            setError(`Failed to update landholding: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleRetryLoad = () => {
        if (currentLandId) fetchLandholdingData(currentLandId).then(() => setError(null)).catch(err => setError(`Failed to reload: ${err.message}`));
        else initializeComponent();
    };

    // ─── Owner form handlers ───────────────────────────────────

    const resetNewOwnerForm = () => {
        setNewOwnerData({
            owner_name: '',
            survey_no: formData.survey_no,
            owner_total_area: remainingTotalArea > 0 ? remainingTotalArea : '',
            owner_aqr_area: remainingAcquiredArea > 0 ? remainingAcquiredArea : '',
            owner_irrigable_area: '',
            owner_non_irrigable_area: '',
            owner_construction_area: '',
        });
    };

    const resetEditOwnerForm = (ownerData = null) => {
        if (ownerData) {
            setEditOwnerData({
                land_owner_id: ownerData.land_owner_id || '',
                owner_name: ownerData.owner_name || '',
                survey_no: ownerData.survey_no || formData.survey_no,
                owner_total_area: ownerData.owner_total_area || '',
                owner_aqr_area: ownerData.owner_aqr_area || '',
                owner_irrigable_area: ownerData.owner_irrigable_area || '',
                owner_non_irrigable_area: ownerData.owner_non_irrigable_area || '',
                owner_construction_area: ownerData.owner_construction_area || '',
            });
            setOriginalOwnerData(ownerData);
            setCurrentEditingOwnerId(ownerData.land_owner_id);
        } else {
            setEditOwnerData({ land_owner_id: '', owner_name: '', survey_no: formData.survey_no, owner_total_area: '', owner_aqr_area: '', owner_irrigable_area: '', owner_non_irrigable_area: '',owner_construction_area: '', });
            setOriginalOwnerData(null);
            setCurrentEditingOwnerId(null);
        }
    };

    const handleShowAddOwnerForm = () => {
        if (showEditOwnerForm) { setShowEditOwnerForm(false); resetEditOwnerForm(); }
        resetNewOwnerForm();
        setShowAddOwnerForm(true);
        setTimeout(() => { if (addOwnerFormRef.current) addOwnerFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' }); });
    };

    const handleShowEditOwnerForm = (owner) => {
        if (showAddOwnerForm) { setShowAddOwnerForm(false); resetNewOwnerForm(); }
        resetEditOwnerForm(owner);
        setShowEditOwnerForm(true);
        setTimeout(() => { if (editOwnerFormRef.current) editOwnerFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' }); });
    };

    const handleDeleteOwner = (ownerId) => {
        setConfirmConfig({
            title: translations[language].DeleteOccupant || 'Delete Occupant',
            message: translations[language].DeleteOccupantConfirm || 'Are you sure you want to delete this occupant?',
            type: 'danger',
            onConfirm: async () => {
                try {
                    const result = await apiService.patchUpdateMeasurement(ownerId, {
                        is_active: 0,
                    });
                    if (result && (result.ok || result.status === 200)) {
                        setTimeout(() => fetchLandholdingData(currentLandId), 500);
                    } else {
                        throw new Error('Failed to delete owner');
                    }
                } catch (err) {
                    console.error('Error deleting owner:', err);
                    setError(`Failed to delete owner: ${err.message}`);
                }
            },
            onCancel: null,
        });
        setShowConfirmModal(true);
    };

    const handleOwnerFieldChange = (setter) => (e) => {
        const { name, value } = e.target;
        let newValue = value;
        if (['owner_total_area', 'owner_aqr_area', 'owner_irrigable_area', 'owner_non_irrigable_area', 'owner_construction_area'].includes(name)) {
            if (value === '') newValue = '';
            else if (/^\d*\.?\d{0,4}$/.test(value)) newValue = value;
            else newValue = safeTwoDecimals(parseFloat(value)).toString();
        }
        setter(prev => ({ ...prev, [name]: newValue }));
    };

    const handleNewOwnerChange = (e) => {
        const { name, value } = e.target;
        let newValue = value;
        if (['owner_total_area', 'owner_aqr_area', 'owner_irrigable_area', 'owner_non_irrigable_area', 'owner_construction_area'].includes(name)) {
            if (value === '') newValue = '';
            else if (/^\d*\.?\d{0,4}$/.test(value)) newValue = value;
            else newValue = safeTwoDecimals(parseFloat(value)).toString();
        }
        if (name === 'owner_total_area' && (parseFloat(value) || 0) > remainingTotalArea) newValue = remainingTotalArea;
        setNewOwnerData(prev => ({ ...prev, [name]: newValue }));
    };

    const handleEditOwnerChange = (e) => {
        const { name, value } = e.target;
        let newValue = value;
        if (['owner_total_area', 'owner_aqr_area', 'owner_irrigable_area', 'owner_non_irrigable_area', 'owner_construction_area'].includes(name)) {
            if (value === '') newValue = '';
            else if (/^\d*\.?\d{0,4}$/.test(value)) newValue = value;
            else newValue = safeTwoDecimals(parseFloat(value)).toString();
        }
        setEditOwnerData(prev => ({ ...prev, [name]: newValue }));
    };

    const handleAddOwner = async (e) => {
        e.preventDefault();
        if (!currentLandId || !formData.project_id || !formData.proposal_id) return;
        try {
            setAddingOwner(true);
            const result = await apiService.addMeasurement({
                project_id: formData.project_id,
                proposal_id: formData.proposal_id,
                LandAcquisition_id: currentLandId,
                owner_name: newOwnerData.owner_name,
                survey_no: formData.survey_no,
                dist_name: formData.district_id,
                taluka_name: formData.taluka_id,
                village_name: formData.village_id,
                owner_total_area: parseFloat(newOwnerData.owner_total_area),
                owner_aqr_area: parseFloat(newOwnerData.owner_aqr_area),
                owner_irrigable_area: parseFloat(newOwnerData.owner_irrigable_area) || 0,
                owner_non_irrigable_area: parseFloat(newOwnerData.owner_non_irrigable_area) || 0,
                ...(newOwnerData.owner_construction_area ? { owner_construction_area: parseFloat(newOwnerData.owner_construction_area) } : {}),
                is_active: 1,
            });
            if (result && (result.ok || result.status === 200)) {
                resetNewOwnerForm();
                setShowAddOwnerForm(false);
                setTimeout(() => fetchLandholdingData(currentLandId), 1000);
                setTimeout(() => {
                    if (typeof onSuccess === 'function') onSuccess();
                    else if (typeof onBack === 'function') onBack();
                }, 2000);
            } else throw new Error('Failed to add owner');
        } catch (err) {
            console.error('Error adding owner:', err);
        } finally {
            setAddingOwner(false);
        }
    };

    const handleEditOwner = async (e) => {
        e.preventDefault();
        if (!currentLandId || !formData.project_id || !formData.proposal_id || !currentEditingOwnerId) return;
        if ((parseFloat(editOwnerData.owner_total_area) || 0) <= 0) return;
        try {
            setEditingOwner(true);
            const result = await apiService.patchUpdateMeasurement(currentEditingOwnerId, {
                project_id: formData.project_id,
                proposal_id: formData.proposal_id,
                LandAcquisition_id: currentLandId,
                owner_name: editOwnerData.owner_name,
                survey_no: formData.survey_no,
                dist_name: formData.district_id,
                taluka_name: formData.taluka_id,
                village_name: formData.village_id,
                ...(editOwnerData.owner_total_area ? { owner_total_area: parseFloat(editOwnerData.owner_total_area) } : {}),
                ...(editOwnerData.owner_aqr_area ? { owner_aqr_area: parseFloat(editOwnerData.owner_aqr_area) } : {}),
                ...(editOwnerData.owner_irrigable_area ? { owner_irrigable_area: parseFloat(editOwnerData.owner_irrigable_area) } : {}),
                ...(editOwnerData.owner_non_irrigable_area ? { owner_non_irrigable_area: parseFloat(editOwnerData.owner_non_irrigable_area) } : {}),
                ...(editOwnerData.owner_construction_area ? { owner_construction_area: parseFloat(editOwnerData.owner_construction_area) } : {}),
            });
            if (result && (result.ok || result.status === 200)) {
                resetEditOwnerForm();
                setShowEditOwnerForm(false);
                setTimeout(() => fetchLandholdingData(currentLandId), 1000);
            } else throw new Error('Failed to update owner');
        } catch (err) {
            console.error('Error updating owner:', err);
        } finally {
            setEditingOwner(false);
        }
    };

    // ─── Loading state ─────────────────────────────────────────

    if (loading) {
        return (
            <div className="bg-white rounded-lg border border-gray-300 shadow-lg w-full max-w-7xl mx-auto my-4 sm:my-6">
                <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
                    <div className="text-gray-600">{translations[language].Loadinglandholdingdata}</div>
                </div>
            </div>
        );
    }

    // ─── Reusable display-only field ───────────────────────────

    const DisplayField = ({ label, value, required }) => (
        <div className="space-y-1 sm:space-y-2">
            <label className="block text-xs sm:text-sm font-medium text-gray-700">
                {label}{required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border border-gray-200 rounded-md bg-gray-50 text-gray-800 min-h-[36px]">
                {value !== undefined && value !== null && value !== '' ? value : <span className="text-gray-400">—</span>}
            </div>
        </div>
    );

    // ─── Spinner SVG ───────────────────────────────────────────

    const Spinner = () => (
        <svg className="animate-spin mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
    );

    // ─── Render ────────────────────────────────────────────────

    return (
        <div className="bg-white rounded-lg border border-gray-300 shadow-lg w-full max-w-7xl mx-auto my-4 sm:my-6">

            {/* Header */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-900 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-t-lg">
                <div className="flex items-center justify-between">
                    <h2 className="text-base sm:text-lg font-semibold flex items-center">
                        <Save className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                        <span className="hidden sm:inline">{translations[language].EditLandholding}</span>
                        <span className="sm:hidden">{translations[language].Edit}</span>
                    </h2>
                    <button onClick={onBack} className="text-white hover:text-gray-200 transition-colors">
                        <X className="h-5 w-5 sm:h-6 sm:w-6" />
                    </button>
                </div>
            </div>

            <div className="p-4 sm:p-6">

                {/* Error banner */}
                {error && (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-md flex items-center justify-between">
                        <span className="text-sm">{error}</span>
                        <button onClick={handleRetryLoad} className="ml-2 text-sm underline hover:no-underline font-medium">
                            {translations[language].Retry}
                        </button>
                    </div>
                )}

                {!currentLandId && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded-md flex items-center justify-between">
                        <span className="text-sm">{translations[language].WarningNolandIDavailableUpdatesmaynotworkproperly}</span>
                        <button onClick={initializeComponent} className="ml-2 text-sm underline hover:no-underline font-medium">
                            {translations[language].Reload}
                        </button>
                    </div>
                )}

                {/* ── Main Edit Form ── */}
                <form onSubmit={handleSubmit}>
                    <div className="space-y-4 sm:space-y-6">

                        {/* Location: display only */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                            <DisplayField label={translations[language]?.District} value={formData.dist_name} required />
                            <DisplayField label={translations[language]?.taluka} value={formData.taluka_name} required />
                            <DisplayField label={translations[language]?.village} value={formData.village_name} required />
                        </div>

                        {/* Survey info + two editable fields */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                            <DisplayField
                                label={translations[language].SurveyType}
                                value={formData.survey_type}
                                required
                            />
                            <DisplayField
                                label={isHouseNumber ? (translations[language].GharNo || 'घर क्रमांक') : translations[language].SurveyNo}
                                value={formData.survey_no}
                                required
                            />
                            <DisplayField label={translations[language].AreaUnit} value={formData.unit_name} required />

                            {/* EDITABLE: Gat/Survey Total Area */}
                            <div className="space-y-1 sm:space-y-2">
                                <label className="block text-xs sm:text-sm font-medium text-gray-700">
                                    {translations[language]?.TotalArea_7_12 || 'Gat/Survey Total Area'} ({formData.unit_name})
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="total_7_12_area"
                                    value={formData.total_7_12_area}
                                    onChange={handleChange}
                                    required
                                    placeholder={translations[language].EnterTotalArea}
                                    className="w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                />
                            </div>

                            {/* Common Area — only shown if value exists in loaded data */}
                            {formData.common_area && (
                                <div className="space-y-1 sm:space-y-2">
                                    <label className="block text-xs sm:text-sm font-medium text-gray-700">
                                        {translations[language]?.commonArea || 'Common Area'} ({formData.unit_name})
                                    </label>
                                    <input
                                        type="text"
                                        name="common_area"
                                        value={formData.common_area}
                                        onChange={handleChange}
                                        placeholder={translations[language]?.EnterCommonArea || 'Enter Common Area'}
                                        className="w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Non-house: Total Area, Acquired Area, Landholding Type (display) */}
                        {/* {!isHouseNumber && (
                            <>
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                                    <DisplayField label={`${translations[language]?.totalArea || 'Total Area'} (${formData.unit_name})`} value={formData.total_area} required />
                                    <DisplayField label={`${translations[language]?.acquiredArea || 'Acquired Area'} (${formData.unit_name})`} value={formData.aqr_area} required />
                                    <DisplayField label={translations[language].SelectLandholdingType} value={formData.land_holding_type} />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                                    <DisplayField label={translations[language]?.landholderName || "Landholder's Name"} value={formData.landholder_name} required />
                                    <DisplayField label={`${translations[language]?.irrigableArea || 'Cultivable Area'} (${formData.unit_name})`} value={formData.irrigable_area} />
                                    <DisplayField label={`${translations[language]?.nonIrrigableArea || 'Non-Cultivable Area'} (${formData.unit_name})`} value={formData.non_irrigable_area} />
                                </div>
                            </>
                        )} */}

                        {/* House: display only */}
                        {isHouseNumber && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                                <DisplayField label="घराचे एकूण क्षेत्र" value={formData.total_area} required />
                                <DisplayField label="बांधकाम क्षेत्र" value={formData.construction_area} required />
                                <DisplayField label="संपादित क्षेत्र" value={formData.aqr_area} required />
                                <DisplayField label="घर मालकाचे नाव" value={formData.landholder_name} required />
                                <DisplayField label={translations[language].SelectLandholdingType} value={formData.land_holding_type} />
                            </div>
                        )}

                        {/* N-E-W-S: display only */}
                        {/* N-E-W-S: editable */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                            {[
                                { name: 'north', label: translations[language].North },
                                { name: 'east', label: translations[language].East },
                                { name: 'west', label: translations[language].West },
                                { name: 'south', label: translations[language].South },
                            ].map(({ name, label }) => (
                                <div key={name} className="space-y-1 sm:space-y-2">
                                    <label className="block text-xs sm:text-sm font-medium text-gray-700">
                                        {label}
                                    </label>
                                    <input
                                        type="text"
                                        name={name}
                                        value={formData[name]}
                                        onChange={handleChange}
                                        placeholder={`Enter ${label}`}
                                        className="w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                    />
                                </div>
                            ))}
                        </div>

                        {/* Form buttons */}
                        <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                            <button type="button" onClick={onBack} disabled={saving}
                                className="w-full sm:w-auto px-4 sm:px-6 py-2 text-xs sm:text-sm bg-gray-100 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-200 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                                {translations[language]?.Cancel || 'Cancel'}
                            </button>
                            <button type="submit" disabled={saving || !currentLandId}
                                className="w-full sm:w-auto px-6 sm:px-8 py-2 text-xs sm:text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium shadow-sm flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed">
                                {saving ? (<><Spinner />{translations[language].Saving}</>) : (
                                    <><Save className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">{translations[language]?.saveChanges || 'Save Changes'}</span><span className="sm:hidden">{translations[language].Save}</span></>
                                )}
                            </button>
                        </div>
                    </div>
                </form>

                {/* ── Owners / Occupants Section ── */}
                <div className="mt-8 border-t pt-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            <User className="h-5 w-5 mr-2" />
                            {translations[language].LandOccupants}
                        </h3>
                        <button onClick={handleShowAddOwnerForm} disabled={!currentLandId}
                            className="px-4 py-2 rounded-md font-medium text-sm flex items-center bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                            <Plus className="h-4 w-4 mr-1" />
                            {translations[language].AddOccupant}
                        </button>
                    </div>

                    {/* Owners table */}
                    {existingOwners.length > 0 ? (
                        <div className="space-y-4 mb-8">
                            <h4 className="text-base font-semibold text-gray-800">{translations[language].ExistingOwners}</h4>
                            <div className="overflow-x-auto shadow-md rounded-lg border border-gray-200 bg-white">
                                <table className="min-w-full table-auto text-sm text-left text-gray-700">
                                    <thead className="bg-blue-100 text-gray-600 font-medium">
                                        <tr>
                                            <th className="px-4 py-3">{translations[language].Number1}</th>
                                            <th className="px-4 py-3">{translations[language].OwnerName}</th>
                                            <th className="px-4 py-3">{translations[language].SurveyNo}</th>
                                            <th className="px-4 py-3">{translations[language].TotalArea} ({formData.unit_name})</th>
                                            <th className="px-4 py-3">{translations[language].AcquiredArea} ({formData.unit_name})</th>
                                            <th className="px-4 py-3">{translations[language].Action}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {existingOwners.map((owner, index) => (
                                            <tr key={owner.land_owner_id || index} className="border-b hover:bg-gray-50 transition">
                                                <td className="px-4 py-3">{index + 1}</td>
                                                <td className="px-4 py-3 font-semibold text-gray-800">{owner.owner_name}</td>
                                                <td className="px-4 py-3">{owner.survey_no}</td>
                                                <td className="px-4 py-3">{owner.owner_total_area}</td>
                                                <td className="px-4 py-3">{owner.owner_aqr_area}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => handleShowEditOwnerForm(owner)}
                                                            className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-xs font-medium">
                                                            {translations[language].Edit}
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteOwner(owner.land_owner_id)}
                                                            className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-xs font-medium">
                                                            {translations[language].Delete || 'Delete'}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg mb-6">
                            <User className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                            <p>{translations[language].NoOwnersFound}</p>
                        </div>
                    )}

                    {/* Edit Owner Form */}
                    {showEditOwnerForm && (
                        <div ref={editOwnerFormRef} className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="text-lg font-semibold text-blue-900">{translations[language].EditOccupant}</h4>
                                <button
                                    onClick={() => { setShowEditOwnerForm(false); resetEditOwnerForm(); }}
                                    className="text-blue-600 hover:text-blue-800">
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                            <form onSubmit={handleEditOwner}>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">{translations[language].OwnerName}<span className="text-red-500 ml-1">*</span></label>
                                        <input type="text" name="owner_name" value={editOwnerData.owner_name} onChange={handleEditOwnerChange} required
                                            placeholder="Enter owner name" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div>
                                    {/* <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">{translations[language].SurveyNo}<span className="text-red-500 ml-1">*</span></label>
                                        <input type="text" name="survey_no" value={editOwnerData.survey_no} readOnly className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50" />
                                    </div> */}
                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            {isHouseNumber
                                                ? (translations[language].GharNo || 'घर क्रमांक')
                                                : translations[language].SurveyNo}
                                            <span className="text-red-500 ml-1">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="survey_no"
                                            value={editOwnerData.survey_no}
                                            readOnly
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50"
                                        />
                                    </div>
                                    {/* <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">{translations[language].TotalArea} ({formData.unit_name})<span className="text-red-500 ml-1">*</span></label>
                                        <input type="text" name="owner_total_area" value={editOwnerData.owner_total_area} onChange={handleEditOwnerChange} required
                                            placeholder={translations[language].EnterTotalArea} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">{translations[language].AcquiredArea} ({formData.unit_name})<span className="text-red-500 ml-1">*</span></label>
                                        <input type="text" name="owner_aqr_area" value={editOwnerData.owner_aqr_area} onChange={handleEditOwnerChange} required
                                            placeholder={translations[language].EnterAcquiredArea} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">{translations[language].IrrigableArea} ({formData.unit_name})</label>
                                        <input type="text" name="owner_irrigable_area" value={editOwnerData.owner_irrigable_area} onChange={handleEditOwnerChange}
                                            placeholder={translations[language].EnterIrrigableeArea} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">{translations[language].NonIrrigableArea} ({formData.unit_name})</label>
                                        <input type="text" name="owner_non_irrigable_area" value={editOwnerData.owner_non_irrigable_area} onChange={handleEditOwnerChange}
                                            placeholder={translations[language].EnterNonIrrigableArea} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div> */}
                                    {!isHouseNumber ? (
                                        <>
                                            <div className="space-y-2">
                                                <label className="block text-sm font-medium text-gray-700">
                                                   {translations[language]?.TotalArea_7_12 }
                                                    <span className="text-red-500 ml-1">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="owner_total_area"
                                                    value={editOwnerData.owner_total_area}
                                                    onChange={handleEditOwnerChange}
                                                    required
                                                    placeholder="Enter Total Area"
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <label className="block text-sm font-medium text-gray-700">
                                                   {translations[language]?.AcquiredArea}
                                                    <span className="text-red-500 ml-1">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="owner_aqr_area"
                                                    value={editOwnerData.owner_aqr_area}
                                                    onChange={handleEditOwnerChange}
                                                    required
                                                    placeholder="Enter Acquired Area"
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <label className="block text-sm font-medium text-gray-700">
                                                    {translations[language]?.culti}
                                                </label>
                                                <input
                                                    type="text"
                                                    name="owner_irrigable_area"
                                                    value={editOwnerData.owner_irrigable_area}
                                                    onChange={handleEditOwnerChange}
                                                    placeholder="Enter Cultivable Area"
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <label className="block text-sm font-medium text-gray-700">
                                                {translations[language]?.nonculti}
                                                </label>
                                                <input
                                                    type="text"
                                                    name="owner_non_irrigable_area"
                                                    value={editOwnerData.owner_non_irrigable_area}
                                                    onChange={handleEditOwnerChange}
                                                    placeholder="Enter Non-Cultivable Area"
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                                />
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="space-y-2">
                                                <label className="block text-sm font-medium text-gray-700">
                                                    घराचे एकूण क्षेत्र
                                                    <span className="text-red-500 ml-1">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="owner_total_area"
                                                    value={editOwnerData.owner_total_area}
                                                    onChange={handleEditOwnerChange}
                                                    required
                                                    placeholder="घराचे एकूण क्षेत्र"
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <label className="block text-sm font-medium text-gray-700">
                                                    बांधकाम क्षेत्र
                                                    <span className="text-red-500 ml-1">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="owner_construction_area"
                                                    value={editOwnerData.owner_construction_area}
                                                    onChange={handleEditOwnerChange}
                                                    required
                                                    placeholder="बांधकाम क्षेत्र"
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <label className="block text-sm font-medium text-gray-700">
                                                    संपादित क्षेत्र
                                                    <span className="text-red-500 ml-1">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="owner_aqr_area"
                                                    value={editOwnerData.owner_aqr_area}
                                                    onChange={handleEditOwnerChange}
                                                    required
                                                    placeholder="संपादित क्षेत्र"
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                                <div className="flex justify-end gap-3 mt-6">
                                    <button type="button" onClick={() => { setShowEditOwnerForm(false); resetEditOwnerForm(); }} disabled={editingOwner}
                                        className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-200 transition-colors font-medium text-sm">
                                        {translations[language].Cancel}
                                    </button>
                                    <button type="submit" disabled={editingOwner}
                                        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium text-sm flex items-center disabled:opacity-50 disabled:cursor-not-allowed">
                                        {editingOwner ? (<><Spinner />{translations[language].Updating}</>) : (<><Save className="h-4 w-4 mr-1" />{translations[language].UpdateOccupant}</>)}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Add Owner Form */}
                    {showAddOwnerForm && (
                        <div ref={addOwnerFormRef} className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="text-lg font-semibold text-blue-900">{translations[language].AddNewOccupantBtn}</h4>
                                <button onClick={() => { setShowAddOwnerForm(false); resetNewOwnerForm(); }} className="text-blue-600 hover:text-blue-800">
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                            <form onSubmit={handleAddOwner}>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">{translations[language].OwnerName}<span className="text-red-500 ml-1">*</span></label>
                                        <input type="text" name="owner_name" value={newOwnerData.owner_name} onChange={handleNewOwnerChange} required
                                            placeholder="Enter owner name" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div>
                                    {/* <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">{translations[language].SurveyNo}<span className="text-red-500 ml-1">*</span></label>
                                        <input type="text" name="survey_no" value={newOwnerData.survey_no} readOnly className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50" />
                                    </div> */}
                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            {isHouseNumber
                                                ? (translations[language].GharNo || 'घर क्रमांक')
                                                : translations[language].SurveyNo}
                                            <span className="text-red-500 ml-1">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="survey_no"
                                            value={newOwnerData.survey_no}
                                            readOnly
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50"
                                        />
                                    </div>
                                    {/* <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">{translations[language].TotalArea} ({formData.unit_name})<span className="text-red-500 ml-1">*</span></label>
                                        <input type="text" name="owner_total_area" value={newOwnerData.owner_total_area} onChange={handleNewOwnerChange} required
                                            placeholder={translations[language].EnterTotalArea} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">{translations[language].AcquiredArea} ({formData.unit_name})<span className="text-red-500 ml-1">*</span></label>
                                        <input type="text" name="owner_aqr_area" value={newOwnerData.owner_aqr_area} onChange={handleNewOwnerChange} required
                                            placeholder={translations[language].EnterAcquiredArea} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">{translations[language].IrrigableArea} ({formData.unit_name})</label>
                                        <input type="text" name="owner_irrigable_area" value={newOwnerData.owner_irrigable_area} onChange={handleNewOwnerChange}
                                            placeholder={translations[language].EnterIrrigableeArea} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">{translations[language].NonIrrigableArea} ({formData.unit_name})</label>
                                        <input type="text" name="owner_non_irrigable_area" value={newOwnerData.owner_non_irrigable_area} onChange={handleNewOwnerChange}
                                            placeholder={translations[language].EnterNonIrrigableArea} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div> */}
                                    {!isHouseNumber ? (
        <>
            <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                    Total Area
                    <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                    type="text"
                    name="owner_total_area"
                    value={newOwnerData.owner_total_area}
                    onChange={handleNewOwnerChange}
                    required
                    placeholder="Enter Total Area"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
            </div>

            <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                    Acquired Area
                    <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                    type="text"
                    name="owner_aqr_area"
                    value={newOwnerData.owner_aqr_area}
                    onChange={handleNewOwnerChange}
                    required
                    placeholder="Enter Acquired Area"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
            </div>

            <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                    CULTIVABLE AREA
                </label>
                <input
                    type="text"
                    name="owner_irrigable_area"
                    value={newOwnerData.owner_irrigable_area}
                    onChange={handleNewOwnerChange}
                    placeholder="Enter Cultivable Area"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
            </div>

            <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                    NON-CULTIVABLE AREA
                </label>
                <input
                    type="text"
                    name="owner_non_irrigable_area"
                    value={newOwnerData.owner_non_irrigable_area}
                    onChange={handleNewOwnerChange}
                    placeholder="Enter Non-Cultivable Area"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
            </div>
        </>
    ) : (
        <>
            <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                    घराचे एकूण क्षेत्र
                    <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                    type="text"
                    name="owner_total_area"
                    value={newOwnerData.owner_total_area}
                    onChange={handleNewOwnerChange}
                    required
                    placeholder="घराचे एकूण क्षेत्र"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
            </div>

            <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                    बांधकाम क्षेत्र
                    <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                    type="text"
                    name="owner_construction_area"
                    value={newOwnerData.owner_construction_area}
                    onChange={handleNewOwnerChange}
                    required
                    placeholder="बांधकाम क्षेत्र"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
            </div>

            <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                    संपादित क्षेत्र
                    <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                    type="text"
                    name="owner_aqr_area"
                    value={newOwnerData.owner_aqr_area}
                    onChange={handleNewOwnerChange}
                    required
                    placeholder="संपादित क्षेत्र"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
            </div>
        </>
    )}
                                </div>
                                <div className="flex justify-end gap-3 mt-6">
                                    <button type="button" onClick={() => { setShowAddOwnerForm(false); resetNewOwnerForm(); }} disabled={addingOwner}
                                        className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-200 transition-colors font-medium text-sm">
                                        {translations[language].cancel}
                                    </button>
                                    <button type="submit" disabled={addingOwner}
                                        className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium text-sm flex items-center disabled:opacity-50 disabled:cursor-not-allowed">
                                        {addingOwner ? (<><Spinner />{translations[language].Adding}</>) : (<><Plus className="h-4 w-4 mr-1" />{translations[language].AddNewOccupantBtn}</>)}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Area distribution summary */}
                    <div className="mb-6">
                        <div className="flex flex-col lg:flex-row gap-6">
                            {/* Total Area Summary */}
                            <div className="flex-1 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                                <h3 className="text-lg text-center font-semibold text-blue-900 mb-3">{translations[language].TotalAreaDistributionSummary}</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-blue-800">{formData.total_area}</div>
                                        <div className="text-sm text-blue-600">{translations[language].LandTotalArea} ({formData.unit_name})</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-green-800">{totalCompletedOwnerArea.toFixed(4)}</div>
                                        <div className="text-sm text-green-600">{translations[language].DistributedToOwners} ({formData.unit_name})</div>
                                    </div>
                                    <div className="text-center">
                                        <div className={`text-2xl font-bold ${remainingTotalArea > 0 ? 'text-orange-800' : 'text-green-800'}`}>{remainingTotalArea.toFixed(4)}</div>
                                        <div className={`text-sm ${remainingTotalArea > 0 ? 'text-orange-600' : 'text-green-600'}`}>{translations[language].Remaining} ({formData.unit_name})</div>
                                    </div>
                                </div>
                                {isTotalAreaComplete && (
                                    <div className="mt-3 p-2 bg-green-100 border border-green-300 rounded-md text-center">
                                        <span className="text-green-800 font-medium">✓ {translations[language].AllAreaDistributed}</span>
                                    </div>
                                )}
                            </div>

                            {/* Acquired Area Summary */}
                            <div className="flex-1 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                                <h3 className="text-lg text-center font-semibold text-green-900 mb-3">{translations[language].AcquiredAreaDistributionSummary}</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-green-800">{formData.aqr_area}</div>
                                        <div className="text-sm text-green-600">{translations[language].LandAcquiredArea} ({formData.unit_name})</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-blue-800">{totalCompletedAcquiredArea.toFixed(4)}</div>
                                        <div className="text-sm text-blue-600">{translations[language].DistributedToOwners} ({formData.unit_name})</div>
                                    </div>
                                    <div className="text-center">
                                        <div className={`text-2xl font-bold ${remainingAcquiredArea > 0 ? 'text-orange-800' : 'text-green-800'}`}>{remainingAcquiredArea.toFixed(4)}</div>
                                        <div className={`text-sm ${remainingAcquiredArea > 0 ? 'text-orange-600' : 'text-green-600'}`}>{translations[language].Remaining} ({formData.unit_name})</div>
                                    </div>
                                </div>
                                {isAcquiredAreaComplete && (
                                    <div className="mt-3 p-2 bg-green-100 border border-green-300 rounded-md text-center">
                                        <span className="text-green-800 font-medium">✓ {translations[language].AllAreaDistributed}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirm Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-sm mx-4 p-6">
                        <div className={`w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center ${confirmConfig.type === 'danger' ? 'bg-red-100' : confirmConfig.type === 'warning' ? 'bg-yellow-100' : 'bg-blue-100'}`}>
                            <span className={`font-bold text-xl ${confirmConfig.type === 'danger' ? 'text-red-600' : confirmConfig.type === 'warning' ? 'text-yellow-600' : 'text-blue-600'}`}>
                                {confirmConfig.type === 'danger' ? '✕' : confirmConfig.type === 'warning' ? '⚠' : '?'}
                            </span>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">{confirmConfig.title}</h3>
                        <p className="text-gray-600 text-sm mb-6 text-center">{confirmConfig.message}</p>
                        <div className="flex gap-3">
                            <button onClick={() => { setShowConfirmModal(false); if (confirmConfig.onCancel) confirmConfig.onCancel(); }}
                                className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors">
                                {translations[language].Cancel}
                            </button>
                            <button onClick={() => { setShowConfirmModal(false); if (confirmConfig.onConfirm) confirmConfig.onConfirm(); }}
                                className={`flex-1 py-2 px-4 text-white rounded-md transition-colors ${confirmConfig.type === 'danger' ? 'bg-red-600 hover:bg-red-700' : confirmConfig.type === 'warning' ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
                                {translations[language].Confirm}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EditLandholdingList;